import { Box, Chip, Stack, Typography } from '@mui/material'
import { Cake as CakeIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { formatDateDMY } from '@/utils/date'
import type { WidgetProps } from './WidgetProps'
import { EmptyHint } from './EmptyHint'

interface BirthdayRow {
  id: number
  name: string
  birthday: string
  days_until: number
}

const GROUPS = [
  { key: 'today', labelKey: 'dashboard.widgets.birthdays.today' },
  { key: 'in_7_days', labelKey: 'dashboard.widgets.birthdays.in7Days' },
  { key: 'in_30_days', labelKey: 'dashboard.widgets.birthdays.in30Days' },
] as const

export function BirthdaysWidget({ payload }: WidgetProps) {
  const { t } = useTranslation()

  const total = GROUPS.reduce(
    (sum, group) => sum + ((payload[group.key] as BirthdayRow[] | undefined)?.length ?? 0),
    0,
  )
  if (total === 0) {
    return <EmptyHint fallbackKey="dashboard.widgets.birthdays.empty" />
  }

  return (
    <Stack spacing={1.5}>
      {GROUPS.map((group) => {
        const rows = (payload[group.key] ?? []) as BirthdayRow[]
        if (rows.length === 0) return null

        return (
          <Box key={group.key}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t(group.labelKey)}
            </Typography>
            <Stack spacing={0.5}>
              {rows.map((row) => (
                <Box key={row.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  {group.key === 'today' && <CakeIcon fontSize="small" color="warning" />}
                  <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                    {row.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {formatDateDMY(row.birthday)}
                  </Typography>
                  {group.key !== 'today' && (
                    <Chip
                      size="small"
                      label={t('dashboard.widgets.birthdays.inDays', { count: row.days_until })}
                    />
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        )
      })}
    </Stack>
  )
}
